import { createContext, useContext, useState, useRef, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FlyAnimationContextType {
    triggerFly: (startRect: DOMRect, imageSrc: string, onComplete?: () => void) => void;
    cartIconRef: React.RefObject<HTMLButtonElement>;
}

const FlyAnimationContext = createContext<FlyAnimationContextType | null>(null);

export const useFlyAnimation = () => {
    const context = useContext(FlyAnimationContext);
    if (!context) {
        throw new Error('useFlyAnimation must be used within FlyAnimationProvider');
    }
    return context;
};

interface FlyAnimationProviderProps {
    children: ReactNode;
}

export function FlyAnimationProvider({ children }: FlyAnimationProviderProps) {
    const [flyingImage, setFlyingImage] = useState<{
        src: string;
        startX: number;
        startY: number;
        endX: number;
        endY: number;
    } | null>(null);
    const cartIconRef = useRef<HTMLButtonElement>(null);

    const triggerFly = (startRect: DOMRect, imageSrc: string, onComplete?: () => void) => {

        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
            // Try to get cart icon ref, with a small delay to ensure it's mounted
            const getCartIcon = () => {
                console.log('[FlyAnimation] cartIconRef.current:', cartIconRef.current);
                if (cartIconRef.current) {
                    return cartIconRef.current;
                }
                // Fallback: try to find cart button by querySelector
                const cartButton = document.querySelector('[data-cart-icon]') as HTMLButtonElement;
                console.log('[FlyAnimation] Fallback querySelector result:', cartButton);
                if (cartButton) {
                    return cartButton;
                }
                return null;
            };

            const cartButton = getCartIcon();
            if (!cartButton) {
                console.warn('[FlyAnimation] Cart icon not found, skipping animation');
                onComplete?.();
                return;
            }

            const cartRect = cartButton.getBoundingClientRect();
            const startX = startRect.left + startRect.width / 2;
            const startY = startRect.top + startRect.height / 2;
            const endX = cartRect.left + cartRect.width / 2;
            const endY = cartRect.top + cartRect.height / 2;

            console.log('[FlyAnimation] Animation positions:', { startX, startY, endX, endY, cartRect });

            setFlyingImage({
                src: imageSrc,
                startX,
                startY,
                endX,
                endY,
            });

            // Call onComplete after animation duration (0.8s)
            setTimeout(() => {
                setFlyingImage(null);
                onComplete?.();
            }, 800);
        });
    };

    return (
        <FlyAnimationContext.Provider value={{ triggerFly, cartIconRef }}>
            {children}
            <AnimatePresence>
                {flyingImage && (
                    <motion.img
                        key={`fly-${Date.now()}`}
                        src={flyingImage.src}
                        alt="Flying product"
                        style={{
                            position: 'fixed',
                            left: 0,
                            top: 0,
                            width: 64,
                            height: 64,
                            objectFit: 'cover',
                            borderRadius: 8,
                            zIndex: 9999,
                            pointerEvents: 'none',
                        }}
                        initial={{
                            x: flyingImage.startX - 32,
                            y: flyingImage.startY - 32,
                            scale: 1,
                            opacity: 1,
                        }}
                        animate={{
                            x: flyingImage.endX - 32,
                            y: flyingImage.endY - 32,
                            scale: 0.3,
                            opacity: 0.8,
                        }}
                        exit={{
                            opacity: 0,
                            scale: 0,
                        }}
                        transition={{
                            duration: 0.8,
                            ease: 'easeInOut',
                        }}
                        onAnimationStart={() => console.log('[FlyAnimation] Animation started')}
                        onAnimationComplete={() => console.log('[FlyAnimation] Animation completed')}
                    />
                )}
            </AnimatePresence>
        </FlyAnimationContext.Provider>
    );
}

export default FlyAnimationProvider;
